import { crore, money, usd } from "../lib/format.ts";
import { gapOf } from "../lib/gap.ts";

export type Tab = "calculator" | "expenses" | "repatriation";

export interface SummaryData {
  /** Corpus needed at 90% confidence; null while the simulation runs. */
  corpus: number | null;
  yearOneGross: number | null;
  spending: { annualTotal: number; standard: boolean; cityName: string };
  /** The lifestyle figure the calculator is actually using. */
  lifestyleUsed: number;
  /** True when that figure is the spending plan's total, unchanged. */
  planInUse: boolean;
  savings: { grossInr: number; grossUsd: number; netInr: number; isExample: boolean; otherCounted: number };
}

/** "At a glance": one figure from each tab, visible on landing and on every tab. */
export function Summary({
  data,
  onOpen,
  onPrint,
  onUsePlan,
}: {
  data: SummaryData;
  onOpen: (tab: Tab) => void;
  onPrint: () => void;
  onUsePlan: () => void;
}) {
  const { corpus, savings } = data;
  const total = savings.grossInr + savings.otherCounted;
  const gross = corpus !== null ? gapOf(corpus, total) : null;
  const net = corpus !== null ? gapOf(corpus, savings.netInr + savings.otherCounted) : null;

  return (
    <section className="summary" aria-labelledby="summary-title">
      <div className="summary-head">
        <h2 id="summary-title">At a glance</h2>
        <span className="summary-actions">
          <a className="link-quiet" href="#feedback">Give feedback</a>
          <button type="button" className="secondary-btn" onClick={onPrint}>
            Print summary
          </button>
        </span>
      </div>

      <div className="summary-cards">
        <button type="button" className="summary-card" onClick={() => onOpen("calculator")}>
          <span className="sc-label">Corpus needed</span>
          <span className="sc-value">{corpus !== null ? crore(corpus) : "Calculating…"}</span>
          <span className="sc-sub">
            90% confidence
            {data.yearOneGross !== null ? ` · first year ${money(data.yearOneGross)}` : ""}
          </span>
        </button>

        {/* A div, not a button: it holds two actions, and buttons cannot nest. */}
        <div className="summary-card">
          <button type="button" className="sc-open" onClick={() => onOpen("expenses")}>
            <span className="sc-label">
              Living costs used
              {data.planInUse && data.spending.standard && <span className="badge">Standard</span>}
            </span>
            <span className="sc-value">
              {money(data.lifestyleUsed)}
              <small> / yr</small>
            </span>
            <span className="sc-sub">
              {data.planInUse
                ? `From your spending plan · ${data.spending.cityName}`
                : `Your own amount · spending plan is ${money(data.spending.annualTotal)}`}
            </span>
          </button>
          {!data.planInUse && (
            <button type="button" className="sc-action" onClick={onUsePlan}>
              Use spending plan instead
            </button>
          )}
        </div>

        <button type="button" className="summary-card" onClick={() => onOpen("repatriation")}>
          <span className="sc-label">
            Your savings
            {savings.isExample && <span className="badge">Example US</span>}
          </span>
          <span className="sc-value">{crore(total, 2)}</span>
          <span className="sc-sub">
            US {crore(savings.grossInr, 2)} ({usd(savings.grossUsd)}) · other {crore(savings.otherCounted, 2)}
          </span>
        </button>

        <button
          type="button"
          className={`summary-card gap-card ${gross ? gross.status : ""}`}
          onClick={() => onOpen("calculator")}
        >
          <span className="sc-label">
            {gross ? gross.label : "Difference"}
            {savings.isExample && <span className="badge">Example</span>}
          </span>
          <span className="sc-value">{gross ? crore(gross.amount, 2) : "—"}</span>
          <span className="sc-sub">
            {net ? `US after tax: ${net.label.toLowerCase()} ${crore(net.amount, 2)}` : "Corpus vs your savings"}
          </span>
        </button>
      </div>
    </section>
  );
}
